import _ from "lodash";
import { END, eventChannel } from "redux-saga";
import { call, put, SagaGenerator, select, take, takeLatest } from "typed-redux-saga";
import { AnyAction } from "typescript-fsa";

import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Model, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { Commands } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import {
	findElementByFormModelPath,
	FormModel,
	isFormModel
} from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import { DocumentModel, DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import {
	ANTRAG_DETAIL_ACTIVITY_ID,
	TASK_DETAIL_ACTIVITY_ID,
	taskDocumentLoaded
} from "../../modules/processOverview/store/actions";
import { isWorkflowActivityWithLoadedData } from "../../utilities/activityUtils";
import { fetchFromServerConnectorAsBase64 } from "../../utilities/restHelper";
import { VALIDATION_ACTIONS } from "../../utilities/workflowUtils";
import { QUERY_PATH_TO_VALIDATION_TYP } from "../../utilities/variables/documentModelPaths";
import { IMetaGroup } from "../../workaround/internal/IMetaGroup";
import { IMetaRule } from "../../workaround/internal/IMetaRule";
import { IMetaModel } from "../../workaround/internal/IMetaModel";
import { MetaKeys } from "../../workaround/internal/MetaKeys";
import { StammdatenFormDescriptor } from "../../utilities/descriptors";

import { getValidationCode, getValidationTargetFieldPaths } from "../slices/validation/selectors";
import { setValidationCode, setValidationTargetFieldPaths } from "../slices/validation/reducers";
import { addDataHolder } from "../actions";

// We need to use the require syntax for the web worker since native Webpack web worker support isn't available due to our CommonJs module format
// eslint-disable-next-line
const Worker = require("./webWorkers/requiredFieldsCollector.worker");
const documentService = new DocumentServiceFactory().getDocumentService();
const logger = LoggerFactory.getLogger("validation");

/**
 * A saga that runs validations on the document using a worker thread in order to identify fields
 * that are currently required. The IDs of these fields will be stored in a data holder of the current
 * activity and can then be used when rendering to mark the fields as required.
 */
export function* requiredFieldsSaga(): SagaGenerator<void> {
	yield* takeLatest(
		(action: AnyAction) =>
			taskDocumentLoaded.match(action) ||
			ActivityActions.setData.match(action) ||
			(FormEngineActions.command.match(action) &&
				(Commands.changeScreen.match(action.payload.engineEvent) ||
					Commands.pushScreen.match(action.payload.engineEvent) ||
					Commands.dropScreen.match(action.payload.engineEvent) ||
					Commands.setLocationStack.match(action.payload.engineEvent))),
		function* (action) {
			if (FormEngineActions.command.match(action)) {
				const activity = yield* select(ActivitySelectors.activityById(action.payload.activityId));
				if (StammdatenFormDescriptor.isInstance(activity?.descriptor)) {
					return;
				}
				yield* call(setRequiredFields, action.payload.activityId);
			} else {
				yield* call(setRequiredFields, TASK_DETAIL_ACTIVITY_ID);
				yield* call(setRequiredFields, ANTRAG_DETAIL_ACTIVITY_ID);
			}
		}
	);
}

function* setRequiredFields(activityId: string): SagaGenerator<void> {
	const activity = yield* select(ActivitySelectors.activityById(activityId));

	if (!activity || !isWorkflowActivityWithLoadedData(activity)) {
		return;
	}

	if (!activity.dataHolders?.some(it => "validationInfo" in it.descriptor)) {
		try {
			const validationInfoDataHolder: Activity.DataHolder = {
				descriptor: { validationInfo: "validationInfo" },
				dirty: false,
				savingState: "saved",
				loadingState: "missing",
				slices: {}
			};

			yield* put(addDataHolder({ dataHolder: validationInfoDataHolder, activityId }));
		} catch (e) {
			logger.log(e);
		}
	}

	try {
		yield* put(
			ActivityActions.setDataByDescriptor({
				descriptor: { validationInfo: "validationInfo" },
				activityId,
				data: {
					requiredFields: yield* call(findRequiredFields, activity)
				}
			})
		);
	} catch (e) {
		logger.log(e);
	}
}

function* findRequiredFields(activity: Activity): SagaGenerator<string[]> {
	const documentAndValidationModel = yield* select(
		ModelSelectors.modelInScene(
			{
				activityId: activity.id,
				modelType: "document"
			},
			Model.isDocumentAndValidationModel
		)
	);

	if (!documentAndValidationModel || isStammdatenDocumentModel(documentAndValidationModel)) {
		return [];
	}

	const data = yield* select(ActivitySelectors.data(activity.id));
	if (!data) {
		return [];
	}

	const document = _.cloneDeep(data as FormActivity.Data.SingleDocumentData).document;
	_.set(document, QUERY_PATH_TO_VALIDATION_TYP, VALIDATION_ACTIONS.COMPLETE);

	const worker = new Worker.default();
	const channel = eventChannel(emitter => {
		worker.onmessage = (e: any) => {
			// Sends the event to the saga
			emitter({ requiredFieldIds: e.data });
			worker.terminate();
			emitter(END);
		};
		// Returns an unsubscribe method
		return () => {
			worker.terminate();
		};
	});

	const modelId = documentAndValidationModel.header.id;
	let validationCode = yield* select(getValidationCode(modelId));

	if (!validationCode) {
		validationCode = yield* call(
			fetchFromServerConnectorAsBase64,
			{
				method: "GET",
				relativeUrl: `/v2/models/${documentAndValidationModel.header.id}/validationCode`
			},
			"utf8" as const
		);
		if (!validationCode) {
			return [];
		}

		yield* put(setValidationCode([modelId, validationCode]));
	}

	worker.postMessage(
		JSON.stringify({
			documentModel: {
				content: documentAndValidationModel.content,
				header: documentAndValidationModel.header
			},
			validationCode,
			document: documentService.formatDates(document, documentAndValidationModel),
			referencedFieldPaths: yield* call(getOrFindRelevantValidationTargetFieldPaths, activity.id)
		})
	);

	const { requiredFieldIds } = (yield* take(channel)) as { requiredFieldIds: string[] };
	if (!requiredFieldIds) {
		return [];
	}

	return requiredFieldIds;
}

/**
 * Recursively retrieves validation rules (IMetaRule) from the document model.
 * @param group The current group to search for validation rules.
 * @param path The current model path that points to this group. Empty if root.
 * @param metaModel The IMetaModel containing validation info
 */
export function getRules({
	group,
	path,
	metaModel
}: {
	group: DocumentModel.Group;
	path?: ModelPath;
	metaModel: IMetaModel;
}) {
	let rules: IMetaRule[] = [];

	if (path) {
		const groupMeta: IMetaGroup = metaModel.getValue(MetaKeys.MODEL_META_GROUP, ModelPath.toString(path));
		groupMeta.getValue(MetaKeys.GROUP_RULE_LIST)?.forEach((it: IMetaRule) => rules.push(it));
	}

	for (const el of group.elements) {
		if (el.type === "Group") {
			rules = [
				...rules,
				...getRules({ group: el, path: [...(path ?? []), { elementName: el.name }], metaModel })
			];
		}
	}
	return rules;
}

/**
 * Identifies which fields in the model are a) the target of validation rules and b) visible in the activity's current screen.
 * Uses or fills a cache in the store's validation slice.
 * @param activityId The Form Engine activity for which to identify relevant validation target paths.
 */
function* getOrFindRelevantValidationTargetFieldPaths(activityId: string): SagaGenerator<string[]> {
	const formModel = yield* select(ModelSelectors.modelInScene({ activityId, modelType: "form" }, isFormModel));
	if (!formModel) {
		return [];
	}

	const uiState = yield* select(FormEngineSelectors.uiState(activityId));
	if (!uiState) {
		return [];
	}

	const screenState = uiState.screenLocation[uiState.screenLocation.length - 1];

	const cachedValidationTargetFields = yield* select(
		getValidationTargetFieldPaths({
			formModelId: formModel.header.id,
			screenPath: screenState.locationPath
		})
	);

	if (cachedValidationTargetFields) {
		return cachedValidationTargetFields;
	}

	const documentAndValidationModel = yield* select(
		ModelSelectors.modelInScene({ activityId, modelType: "document" }, Model.isDocumentAndValidationModel)
	);
	if (!documentAndValidationModel) {
		return [];
	}

	const metaModel = documentAndValidationModel.generatedCodeAccessor.getMetaModel();
	const rules = getRules({ group: documentAndValidationModel.content.modelRoot, metaModel });

	const ruleReferencedFieldPaths: string[] = rules
		.map(it => it.getValue(MetaKeys.RULE_ERROR_FIELD))
		.map(it => it.getValue(MetaKeys.FIELD_FULLNAME));

	const screenElement = findElementByFormModelPath(formModel, screenState.locationPath);
	if (!screenElement || !FormModel.Screen.isInstance(screenElement)) {
		return [];
	}

	const referencedFieldIds = gatherFieldsFromFormElement(screenElement);
	const documentModelSearchService = new DocumentServiceFactory().getDocumentModelSearchService(
		documentAndValidationModel
	);
	const visibleFieldPaths = referencedFieldIds
		.map(it => documentModelSearchService.getPathById(it))
		.filter(it => it)
		.map(it => ModelPath.toString(<ModelPath>it));

	const validationTargetFieldPaths = visibleFieldPaths.filter(visiblePath =>
		ruleReferencedFieldPaths.find(rulePath =>
			ModelPath.contains(ModelPath.fromString(rulePath), ModelPath.fromString(visiblePath))
		)
	);

	yield* put(
		setValidationTargetFieldPaths({
			formModelId: formModel.header.id,
			screenPath: screenState.locationPath,
			validationTargetFieldPaths
		})
	);
	return validationTargetFieldPaths;
}

function isStammdatenDocumentModel(documentModel: DocumentModel) {
	return documentModel.header.id.toString().endsWith("_S_D");
}

/**
 * Recursively finds references to fields in Form Model elements. These are to be found attached to the rows of a control grid,
 * so this function will drill down through the hierarchy until it finds a control grid.
 * @param formElement
 */
export function gatherFieldsFromFormElement(formElement: any): string[] {
	if (formElement.screenElements) {
		return formElement.screenElements.flatMap((it: any) => gatherFieldsFromFormElement(it));
	} else if (FormModel.ControlGrid.isInstance(formElement)) {
		return (
			formElement.row?.flatMap(it =>
				(it.cell ?? []).flatMap(that => (that.type === "Control" ? that.elementRef : []))
			) ?? []
		);
	} else if (FormModel.EmbeddedRepeat.isInstance(formElement)) {
		return gatherFieldsFromFormElement(formElement.controlGrid);
	}
	return [];
}
